import { RelayPageDataTable } from "@constellation-academy/epic-ui";
import { useCallback, useContext } from "react";
import { useRefetchableFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { videoSourcesTable_QueryFragment$key } from "@relay/videoSourcesTable_QueryFragment.graphql";
import { videoSourcesTable_QueryRefetch } from "@relay/videoSourcesTable_QueryRefetch.graphql";
import { videoSourcesTable_Vimeo_VimeoVideoSourceFragment$key } from "@relay/videoSourcesTable_Vimeo_VimeoVideoSourceFragment.graphql";
import { VIDEO_SOURCES_PER_PAGE } from "./video-sources-table.const";
import { QUERY_FRAGMENT, VIDEO_SOURCE_VIDEO_SOURCE_FRAGMENT } from "./video-sources-table.graphql";
import { VideoSourcesTableProps } from "./video-sources-table.types";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import { VideoSourcesScreenContext } from "../../video-sources.context";
import { VideoSourcesTableActionColumn } from "../video-sources-table-action-column";

export const VideoSourcesTable = ({ queryFragmentRef }: VideoSourcesTableProps) => {
	const { t } = useTranslation("common");
	const { setConnectionId } = useContext(VideoSourcesScreenContext);

	const handleOnConnectionIdChange = (id?: string | null) => {
		id && setConnectionId(id);
	};
	const [_, refetch] = useRefetchableFragment<
		videoSourcesTable_QueryRefetch,
		videoSourcesTable_QueryFragment$key
	>(QUERY_FRAGMENT, queryFragmentRef);

	const refresh = useCallback(() => {
		refetch({}, { fetchPolicy: "network-only" });
	}, [refetch]);

	const canModify = useHasPermissions(["UserInAccountPermission_VimeoVideoSourceAdmin_Modify"]);

	return (
		<RelayPageDataTable<
			videoSourcesTable_QueryRefetch,
			videoSourcesTable_QueryFragment$key,
			videoSourcesTable_Vimeo_VimeoVideoSourceFragment$key
		>
			queryFragment={QUERY_FRAGMENT}
			parentFragmentRef={queryFragmentRef}
			nodeInlineFragment={VIDEO_SOURCE_VIDEO_SOURCE_FRAGMENT}
			extractConnection={(data) => data.Admin.VimeoVideoSource?.VimeoVideoSources}
			rowsCount={VIDEO_SOURCES_PER_PAGE}
			onConnectionIdChange={handleOnConnectionIdChange}
		>
			{(Column) => (
				<>
					<Column header={t("table.name")} body={(videoSource) => videoSource?.name} />
					<Column
						header={t("videoSources.accessTokenLabel")}
						body={(videoSource) => videoSource?.accessToken}
					/>
					<Column
						header={t("videoSources.isDefault")}
						body={(videoSource) => (videoSource?.isDefault ? t("actions.yes") : "")}
					/>
					{canModify && (
						<Column
							header={t("table.actions")}
							body={(videoSource) => (
								<VideoSourcesTableActionColumn
									onRefresh={refresh}
									videoSourceFragmentRef={videoSource}
								/>
							)}
						/>
					)}
				</>
			)}
		</RelayPageDataTable>
	);
};
